import { DateTime, Duration, Interval } from 'luxon'

import { City } from './City'
import { ITimeAndCityLocalizable } from './ITimeAndCityLocalizable'
import { TimeStep } from './TimeStep'
import { ISnapshotLike } from './ISnapshotLike'
import { TimeStepMax } from './TimeStepMax'

const DEFAULT_RADIUS_HOURS = 6

export class Snapshot<T extends TimeStep> implements ITimeAndCityLocalizable, ISnapshotLike<T>, Iterable<T> {
  public readonly city: City
  public readonly timestamp: DateTime

  private history: Array<T> = []
  private reference?: T
  private forecasts: Array<T> = []
  private max?: TimeStepMax
  private period: [DateTime, DateTime]

  constructor(city: City, timestamp: DateTime) {
    this.city = city
    this.timestamp = timestamp.setZone(city.getTimezone())
    this.period = [this.timestamp, this.timestamp]
  }

  setReference(timeStep: T): void {
    this.reference = timeStep
    this.updatePeriod(timeStep.timestamp)
  }

  addHistory(timeStep: T): void {
    this.history.push(timeStep)
    this.updatePeriod(timeStep.timestamp)
  }

  addForecast(timeStep: T): void {
    this.forecasts.push(timeStep)
    this.updatePeriod(timeStep.timestamp)
  }

  private updatePeriod(timestamp: DateTime) {
    const tsSeconds = timestamp.toUTC().toSeconds()
    if (tsSeconds < this.period[0].toUTC().toSeconds()) {
      this.period[0] = timestamp
    }
    if (tsSeconds > this.period[1].toUTC().toSeconds()) {
      this.period[1] = timestamp
    }
  }

  setMax(timeStep: TimeStepMax): void {
    this.max = timeStep
  }

  getReference(): T {
    return this.reference as T
  }

  getHistory(): T[] {
    return this.history
  }

  getForecasts(): T[] {
    return this.forecasts
  }

  /**
   * Half-width (in hours) of the timeline window around `this.timestamp` ("now").
   *
   * The window MUST stay symmetric around "now": the axis labels its centre tick
   * (and the slider expects the "now" ball) at the geometric midpoint of
   * [getStart(), getEnd()]. If the two sides have different extents, that midpoint
   * drifts away from "now".
   *
   * That is exactly the bug this replaces: history is reconstructed as a fixed
   * COUNT of timesteps (forecasts_count). When a few ticks are missing (failed
   * compute tasks leave gaps), the same count reaches further back in wall-clock
   * time, so the history side exceeded DEFAULT_RADIUS_HOURS while the forecast side
   * stayed at 6h. The old per-side logic then produced an asymmetric window and the
   * centre label landed a couple of steps before the now ball.
   *
   * Fix: take the largest half-width either side needs (never below the default
   * radius) and apply it to BOTH sides, keeping "now" exactly at the centre. Empty
   * padding on the shorter side is preferable to a mislabelled centre.
   */
  private getRadiusHours(): number {
    const before = Interval.fromDateTimes(this.period[0], this.timestamp).toDuration('hours').as('hours')
    const after = Interval.fromDateTimes(this.timestamp, this.period[1]).toDuration('hours').as('hours')
    return Math.max(DEFAULT_RADIUS_HOURS, before, after)
  }

  getStart(): DateTime {
    return this.timestamp.minus({ hours: this.getRadiusHours() })
  }

  getEnd(): DateTime {
    return this.timestamp.plus({ hours: this.getRadiusHours() })
  }

  getDurationBefore(): Duration {
    return Interval.fromDateTimes(this.getStart(), this.timestamp).toDuration(['hours'])
  }

  getDurationAfter(): Duration {
    return Interval.fromDateTimes(this.timestamp, this.getEnd()).toDuration(['hours'])
  }

  getMax(): TimeStepMax | null {
    return this.max ? this.max : null
  }

  isEmpty(): boolean {
    for (const timeStep of this.toArray()) {
      if (timeStep && !timeStep.isEmpty()) {
        return false
      }
    }
    return true
  }

  findTimeStepAt(timestamp: DateTime): T | null {
    const lookup = timestamp.toUTC().toSeconds()
    const timeSteps = this.toArray()
    const candidate = timeSteps.find((timeStep: T) => timeStep.timestamp.toUTC().toSeconds() === lookup)

    return candidate ?? null
  }

  nextTimeStep(timestamp: DateTime | null): T | null {
    if (timestamp == null) return null

    const lookup = timestamp.toUTC().toSeconds()
    const timeSteps = this.toArray()
    const candidate = timeSteps.find((timeStep: T, index) =>
      index > 0
        ? timeSteps[index - 1].timestamp.toUTC().toSeconds() === lookup
        : timeSteps[timeSteps.length - 1].timestamp.toUTC().toSeconds() === lookup,
    )

    return candidate ?? null
  }

  *[Symbol.iterator](): Iterator<T> {
    for (const ts of this.toArray()) {
      yield ts
    }
  }

  // referenceHasCumulativeRainfallByRainfall(cumulativeRainfall: number): boolean {
  //   if (!this.reference) return false
  //   return this.reference.hasCumulativeRainfallIntensityByRainfall(cumulativeRainfall)
  // }

  // removeTimeStepsWithoutCumulativeRainfallByRainfall(cumulativeRainfall: number): void {
  //   this.history = this.history.filter((ts) => ts.hasCumulativeRainfallIntensityByRainfall(cumulativeRainfall))

  //   this.forecasts = this.forecasts.filter((ts) => ts.hasCumulativeRainfallIntensityByRainfall(cumulativeRainfall))
  // }

  private toArray(): Array<T> {
    const timeSteps = [...this.history]
    if (this.reference) {
      timeSteps.push(this.reference)
    }
    timeSteps.push(...this.forecasts)

    return timeSteps
  }
}
